import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { faArrowLeft, faCloud, faEnvelope, faWrench, faCalculator, faSpinner, faCheck, faBell } from '@/fontawesome';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import SendTestSCNMessageModal from '@/components/admin/scn/SendTestSCNMessageModal';
import SendTestTelegramMessageModal from '@/components/admin/telegram/SendTestTelegramMessageModal';
import SendTestDiscordMessageModal from '@/components/admin/discord/SendTestDiscordMessageModal';
import SendTestMailModal from '@/components/admin/mails/SendTestMailModal';
import SendTestPushNotificationModal from '@/components/admin/pushNotification/SendTestPushNotificationModal';
import ResendNotificationsPanel from '@/components/admin/tools/ResendNotificationsPanel';
import Modal from '@/components/ui/Modal';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import type { RecalcAllRecommendationsResponse } from '@/modelsAPI';

type ToolsTab = 'test-messages' | 'resend-notifications';

function getTabFromPathname(pathname: string): ToolsTab {
  if (pathname.includes('/resend-notifications')) {
    return 'resend-notifications';
  }
  return 'test-messages';
}

function getPathnameFromTab(tab: ToolsTab): string {
  switch (tab) {
    case 'resend-notifications':
      return '/admin/tools/resend-notifications';
    default:
      return '/admin/tools/test-messages';
  }
}

export default function AdminToolsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = getTabFromPathname(location.pathname);

  // Modal states
  const [isSCNModalOpen, setIsSCNModalOpen] = useState(false);
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  const [isDiscordModalOpen, setIsDiscordModalOpen] = useState(false);
  const [isMailModalOpen, setIsMailModalOpen] = useState(false);
  const [isPushModalOpen, setIsPushModalOpen] = useState(false);

  // Recalc state
  const [isRecalcRecommendationsLoading, setIsRecalcRecommendationsLoading] = useState(false);
  const [isRecalcUsersLoading, setIsRecalcUsersLoading] = useState(false);
  const [recalcRecommendationResult, setRecalcRecommendationResult] = useState<RecalcAllRecommendationsResponse | null>(null);
  const [isRecalcRecommendationResultModalOpen, setIsRecalcRecommendationResultModalOpen] = useState(false);
  const [recalcUserResult, setRecalcUserResult] = useState<{ matched: number, changed: number } | null>(null);
  const [isRecalcUserResultModalOpen, setIsRecalcUserResultModalOpen] = useState(false);

  const handleTabChange = (tab: ToolsTab) => {
    if (tab !== activeTab) {
      navigate(getPathnameFromTab(tab));
    }
  };

  const handleRecalcAllRecommendations = async () => {
    setIsRecalcRecommendationsLoading(true);
    try {
      const result = await APIRoutes.recalcAllRecommendations();
      setRecalcRecommendationResult(result);
      setIsRecalcRecommendationResultModalOpen(true);
      showSuccessToast('Alle Tradingideen wurden neu berechnet');
    } catch (err) {
      handleAPIError(err, 'Fehler beim Neuberechnen der Tradingideen');
    } finally {
      setIsRecalcRecommendationsLoading(false);
    }
  };

  const handleRecalcAllUsers = async () => {
    setIsRecalcUsersLoading(true);
    try {
      const result = await APIRoutes.recalcAllUsers();
      setRecalcUserResult(result);
      setIsRecalcUserResultModalOpen(true);
      showSuccessToast('Alle Nutzer wurden neu berechnet');
    } catch (err) {
      handleAPIError(err, 'Fehler beim Neuberechnen der Nutzer');
    } finally {
      setIsRecalcUsersLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/admin"
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Tools
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('test-messages')}
              className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                activeTab === 'test-messages'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Test Messages
            </button>
            <button
              onClick={() => handleTabChange('resend-notifications')}
              className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                activeTab === 'resend-notifications'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Re-send Notifications
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'test-messages' && (
        <div className="space-y-6">
          {/* Test Messages Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mr-3">
                <FontAwesomeIcon icon={faWrench} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Test-Nachrichten senden
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Senden Sie Testnachrichten über verschiedene Kanäle
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* SCN Button */}
              <button
                onClick={() => setIsSCNModalOpen(true)}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mb-3">
                  <FontAwesomeIcon icon={faCloud} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  SCN Nachricht senden
                </span>
              </button>

              {/* Telegram Button */}
              <button
                onClick={() => setIsTelegramModalOpen(true)}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center mb-3">
                  <FontAwesomeIcon icon={faTelegram} className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Telegram Nachricht senden
                </span>
              </button>

              {/* Discord Button */}
              <button
                onClick={() => setIsDiscordModalOpen(true)}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mb-3">
                  <FontAwesomeIcon icon={faDiscord} className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Discord Nachricht senden
                </span>
              </button>

              {/* Mail Button */}
              <button
                onClick={() => setIsMailModalOpen(true)}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center mb-3">
                  <FontAwesomeIcon icon={faEnvelope} className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Mail senden
                </span>
              </button>

              {/* Push Button */}
              <button
                onClick={() => setIsPushModalOpen(true)}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mb-3">
                  <FontAwesomeIcon icon={faBell} className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Push-Benachrichtigung senden
                </span>
              </button>
            </div>
          </div>

          {/* AutoCalcFields Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mr-3">
                <FontAwesomeIcon icon={faCalculator} className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  AutoCalcFields
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Automatisch berechnete Felder neu berechnen
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Recalc All Recommendations Button */}
              <button
                onClick={handleRecalcAllRecommendations}
                disabled={isRecalcRecommendationsLoading}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mb-3">
                  {isRecalcRecommendationsLoading ? (
                    <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-purple-600 dark:text-purple-400 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faCalculator} className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white text-center">
                  {isRecalcRecommendationsLoading ? 'Wird berechnet...' : 'Alle Tradingideen neu berechnen'}
                </span>
              </button>

              {/* Recalc All Users Button */}
              <button
                onClick={handleRecalcAllUsers}
                disabled={isRecalcUsersLoading}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mb-3">
                  {isRecalcUsersLoading ? (
                    <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faCalculator} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white text-center">
                  {isRecalcUsersLoading ? 'Wird berechnet...' : 'Alle Nutzer neu berechnen'}
                </span>
              </button>

            </div>
          </div>
        </div>
      )}

      {activeTab === 'resend-notifications' && (
        <ResendNotificationsPanel />
      )}

      {/* Modals */}
      <SendTestSCNMessageModal
        isOpen={isSCNModalOpen}
        onClose={() => setIsSCNModalOpen(false)}
        onTestMessageSent={() => {}}
      />

      <SendTestTelegramMessageModal
        isOpen={isTelegramModalOpen}
        onClose={() => setIsTelegramModalOpen(false)}
        onTestMessageSent={() => {}}
      />

      <SendTestDiscordMessageModal
        isOpen={isDiscordModalOpen}
        onClose={() => setIsDiscordModalOpen(false)}
        onTestMessageSent={() => {}}
      />

      <SendTestMailModal
        isOpen={isMailModalOpen}
        onClose={() => setIsMailModalOpen(false)}
        onTestMailSent={() => {}}
      />

      <SendTestPushNotificationModal
        isOpen={isPushModalOpen}
        onClose={() => setIsPushModalOpen(false)}
        onSent={() => {}}
      />

      {/* Recalc Recommendation Result Modal */}
      <Modal
        isOpen={isRecalcRecommendationResultModalOpen}
        onClose={() => setIsRecalcRecommendationResultModalOpen(false)}
        title="Neuberechnung abgeschlossen"
        icon={<FontAwesomeIcon icon={faCheck} className="w-5 h-5 text-green-600" />}
        maxWidth="md"
      >
        <div className="p-6">
          {recalcRecommendationResult !== null && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Die automatisch berechneten Felder aller Tradingideen wurden aktualisiert.
              </p>

              <div className="grid grid-cols-1 gap-3">
                {/* Core Recommendations */}
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Kernstrategie
                  </span>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-blue-600 dark:text-blue-400">{recalcRecommendationResult.coreRecommendationsChanged}</span>
                    <span className="mx-1">/</span>
                    <span>{recalcRecommendationResult.coreRecommendationsMatched}</span>
                    <span className="ml-1">geändert</span>
                  </div>
                </div>

                {/* Earnings Recommendations */}
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Earnings Trades
                  </span>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-green-600 dark:text-green-400">{recalcRecommendationResult.earningsRecommendationsChanged}</span>
                    <span className="mx-1">/</span>
                    <span>{recalcRecommendationResult.earningsRecommendationsMatched}</span>
                    <span className="ml-1">geändert</span>
                  </div>
                </div>

                {/* Swing Trade Recommendations */}
                <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Swing Trades
                  </span>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-purple-600 dark:text-purple-400">{recalcRecommendationResult.swingTradeRecommendationsChanged}</span>
                    <span className="mx-1">/</span>
                    <span>{recalcRecommendationResult.swingTradeRecommendationsMatched}</span>
                    <span className="ml-1">geändert</span>
                  </div>
                </div>

                {/* Long Term Recommendations */}
                <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Langzeit Trades
                  </span>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-orange-600 dark:text-orange-400">{recalcRecommendationResult.longTermRecommendationsChanged}</span>
                    <span className="mx-1">/</span>
                    <span>{recalcRecommendationResult.longTermRecommendationsMatched}</span>
                    <span className="ml-1">geändert</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setIsRecalcRecommendationResultModalOpen(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
                >
                  Schließen
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Recalc User Result Modal */}
      <Modal
        isOpen={isRecalcUserResultModalOpen}
        onClose={() => setIsRecalcUserResultModalOpen(false)}
        title="Neuberechnung abgeschlossen"
        icon={<FontAwesomeIcon icon={faCheck} className="w-5 h-5 text-green-600" />}
        maxWidth="md"
      >
        <div className="p-6">
          {recalcUserResult !== null && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Die automatisch berechneten Felder aller Nutzer wurden aktualisiert.
              </p>

              <div className="grid grid-cols-1 gap-3">

                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-blue-600 dark:text-blue-400">{recalcUserResult.changed}</span>
                    <span className="mx-1">/</span>
                    <span>{recalcUserResult.matched}</span>
                    <span className="ml-1">geändert</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setIsRecalcUserResultModalOpen(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
                >
                  Schließen
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

    </>
  );
}
